import type { UIMessage } from "ai";
import type { IntlShape } from "react-intl";

import {
  type BrandAssistantActivityUpdateData,
  brandAssistantTipsDataSchema,
  getBrandAssistantActivityUpdatesFromParts,
  getBrandAssistantSelectedAssetReferencesFromParts,
  getBrandAssistantSelectedImageReferencesFromParts,
  getBrandAssistantTipsFromParts,
  hasActiveBrandAssistantStreamMetadata,
  parseBrandAssistantChatCompactionEventPart,
  type BrandAssistantChatCompactionEventData,
  type BrandAssistantSelectedAssetReference,
  type BrandAssistantSelectedImageReference,
  type BrandAssistantTipsData,
} from "@runcheetah/shared";

export type AssistantMessagePart = UIMessage["parts"][number];
export type AssistantRuntimeStep = {
  id: string;
  defaultMessage: string;
};
export type AssistantRuntimeStepList = readonly [AssistantRuntimeStep, ...AssistantRuntimeStep[]];
export type AssistantRuntimeThought = AssistantRuntimeStep;
export type AssistantTraceEntry = {
  id: string;
  command: string;
  detail: string | null;
  state: "pending" | "done" | "error";
};
export type AssistantMessageDisplayBlock =
  | { type: "content"; key: string; text: string }
  | { type: "trace"; key: string; entries: AssistantTraceEntry[] };

export type AssistantChatStatus = "ready" | "submitted" | "streaming" | "error";

const ASSISTANT_RUNTIME_THOUGHT_STEPS = [
  {
    id: "assistantChat.runtimeThought.framing",
    defaultMessage: "Framing your request",
  },
  {
    id: "assistantChat.runtimeThought.angle",
    defaultMessage: "Finding a useful angle",
  },
  {
    id: "assistantChat.runtimeThought.reply",
    defaultMessage: "Shaping the reply",
  },
] satisfies AssistantRuntimeStepList;
const ASSISTANT_RUNTIME_THOUGHT_STEP_MS = 2200;
export const ASSISTANT_RUNTIME_THOUGHT_TICK_MS = 100;
const ASSISTANT_LIVE_ACTIVITY_UPDATE_LIMIT = 3;
const LATEST_MESSAGE_SCROLL_THRESHOLD_PX = 64;
const ASSISTANT_TRACE_SEARCH_DISPLAY_LIMIT = 5;
const ASSISTANT_TRACE_NOTE_DISPLAY_LIMIT = 6;
const OPENAI_INLINE_CITATION_PATTERN = /\uE200cite(?:\uE202[^\uE200\uE201\uE202]*)*(?:\uE201)?/g;

export function getAssistantRuntimeThought(
  elapsedMs: number,
  steps: AssistantRuntimeStepList = ASSISTANT_RUNTIME_THOUGHT_STEPS,
): AssistantRuntimeThought {
  const safeElapsedMs = Math.max(0, elapsedMs);
  const activeIndex = Math.min(
    Math.floor(safeElapsedMs / ASSISTANT_RUNTIME_THOUGHT_STEP_MS),
    steps.length - 1,
  );

  return steps[activeIndex] ?? steps[0];
}

export function getAssistantMessageText(message: Pick<UIMessage, "parts">) {
  const text = message.parts
    .map((part) => {
      if (part.type === "text") {
        return part.text;
      }

      if (part.type === "reasoning") {
        return part.text;
      }

      return "";
    })
    .filter(Boolean)
    .join("\n\n");

  return stripAssistantInlineCitationMarkers(text);
}

export function stripAssistantInlineCitationMarkers(text: string) {
  return text
    .replace(OPENAI_INLINE_CITATION_PATTERN, "")
    .replace(/[ \t]+([.,;:!?])/g, "$1")
    .replace(/[ \t]{2,}/g, " ");
}

export function getAssistantActivityParts(
  message: Pick<UIMessage, "parts">,
): AssistantMessagePart[] {
  return message.parts.filter(isRenderableAssistantActivityPart);
}

export function getAssistantTraceEntries(message: Pick<UIMessage, "parts">): AssistantTraceEntry[] {
  return message.parts.flatMap((part, index) => getAssistantTraceEntriesForPart(part, index));
}

export function getAssistantTraceEntriesForPart(
  part: AssistantMessagePart,
  index: number,
): AssistantTraceEntry[] {
  if (!(part.type === "dynamic-tool" || part.type.startsWith("tool-"))) {
    return [];
  }

  const toolName = getAssistantToolName(part);
  if (!toolName) {
    return [];
  }

  const state = getAssistantToolState(part);
  const id = getAssistantToolCallId(part) ?? `${toolName}-${index}`;
  const output = getAssistantToolOutput(part);
  const input = getAssistantToolInput(part);
  const status = getAssistantTraceState(state);

  if (toolName === "web_search") {
    const query = getAssistantWebSearchQuery(input) ?? getAssistantWebSearchQuery(output);
    const sourceCount = getAssistantWebSearchSourceCount(output);
    const detail = getAssistantWebSearchTraceDetail({ sourceCount, status });
    if (!query && status === "done") {
      return [];
    }

    return [
      {
        id,
        command: query ? `search "${query}"` : "search",
        detail: getAssistantToolErrorText(part) ?? detail,
        state: status,
      },
    ];
  }

  if (toolName === "search_knowledge_base") {
    const query = getAssistantKnowledgeBaseQuery(input) ?? getAssistantKnowledgeBaseQuery(output);
    const resultCount = getAssistantKnowledgeBaseResultCount(output);

    return [
      {
        id,
        command: query ? `explaining "${query}"` : "explaining",
        detail:
          getAssistantToolErrorText(part) ?? getAssistantKnowledgeBaseTraceDetail(resultCount),
        state: status,
      },
    ];
  }

  if (toolName === "share_trace_note") {
    const note = getAssistantTraceNote(output) ?? getAssistantTraceNote(input);
    if (!note || note.kind === "reasoning") {
      return [];
    }

    return [
      {
        id,
        command: "insight",
        detail: note.detail,
        state: status,
      },
    ];
  }

  return [];
}

export function getAssistantMessageDisplayBlocks({
  message,
  showContent = true,
  getMessageContent,
}: {
  message: UIMessage;
  showContent?: boolean;
  getMessageContent?: (message: UIMessage) => string;
}): AssistantMessageDisplayBlock[] {
  const blocks: AssistantMessageDisplayBlock[] = [];
  const textParts: AssistantMessagePart[] = [];
  let textStartIndex = 0;
  let visibleSearchTraceCount = 0;
  let visibleNoteTraceCount = 0;

  const flushText = () => {
    if (!showContent || textParts.length === 0) {
      textParts.length = 0;
      return;
    }

    const textMessage = { ...message, parts: [...textParts] } as UIMessage;
    const text = getMessageContent?.(textMessage) ?? getAssistantMessageText(textMessage);
    if (text.trim()) {
      blocks.push({ type: "content", key: `content-${textStartIndex}`, text });
    }

    textParts.length = 0;
  };

  for (const [index, part] of message.parts.entries()) {
    if (part.type === "text" || part.type === "reasoning") {
      if (textParts.length === 0) {
        textStartIndex = index;
      }
      textParts.push(part);
      continue;
    }

    const traceEntries = getVisibleAssistantTraceEntriesForDisplay(
      getAssistantTraceEntriesForPart(part, index),
      {
        visibleSearchTraceCount,
        visibleNoteTraceCount,
      },
    );
    visibleSearchTraceCount += traceEntries.filter(isAssistantSearchTraceEntry).length;
    visibleNoteTraceCount += traceEntries.filter(
      (entry) => !isAssistantSearchTraceEntry(entry),
    ).length;

    if (traceEntries.length > 0) {
      flushText();
      blocks.push({
        type: "trace",
        key: `trace-${traceEntries.map((entry) => entry.id).join("-")}`,
        entries: traceEntries,
      });
    }
  }

  flushText();
  return blocks;
}

function getVisibleAssistantTraceEntriesForDisplay(
  entries: readonly AssistantTraceEntry[],
  counts: { visibleSearchTraceCount: number; visibleNoteTraceCount: number },
) {
  let nextSearchCount = counts.visibleSearchTraceCount;
  let nextNoteCount = counts.visibleNoteTraceCount;

  return entries.filter((entry) => {
    if (isAssistantSearchTraceEntry(entry)) {
      if (entry.state === "pending") {
        return true;
      }

      nextSearchCount += 1;
      return nextSearchCount <= ASSISTANT_TRACE_SEARCH_DISPLAY_LIMIT;
    }

    nextNoteCount += 1;
    return nextNoteCount <= ASSISTANT_TRACE_NOTE_DISPLAY_LIMIT;
  });
}

function isAssistantSearchTraceEntry(entry: AssistantTraceEntry) {
  return (
    entry.command === "search" ||
    entry.command.startsWith("search ") ||
    entry.command === "explaining" ||
    entry.command.startsWith("explaining ")
  );
}

export function getAssistantSelectedAssetReferences(
  message: Pick<UIMessage, "parts">,
): BrandAssistantSelectedAssetReference[] {
  return getBrandAssistantSelectedAssetReferencesFromParts(message.parts);
}

export function getAssistantSelectedImageReferences(
  message: Pick<UIMessage, "parts">,
): BrandAssistantSelectedImageReference[] {
  return getBrandAssistantSelectedImageReferencesFromParts(message.parts);
}

export function getAssistantCompactionEvent(
  message: Pick<UIMessage, "parts">,
): BrandAssistantChatCompactionEventData | null {
  for (const part of message.parts) {
    const event = parseBrandAssistantChatCompactionEventPart(part);
    if (event) {
      return event.data;
    }
  }

  return null;
}

export function hasRenderableAssistantMessageContent(
  message: Pick<UIMessage, "parts">,
  options?: { showActivityParts?: boolean },
) {
  const showActivityParts = options?.showActivityParts ?? true;

  return (
    getAssistantMessageText(message).length > 0 ||
    getAssistantTraceEntries(message).length > 0 ||
    (showActivityParts && getAssistantActivityParts(message).length > 0) ||
    getAssistantSelectedAssetReferences(message).length > 0 ||
    getAssistantSelectedImageReferences(message).length > 0 ||
    getAssistantCompactionEvent(message) !== null
  );
}

export function hasActivePersistedAssistantStream(messages: readonly UIMessage[]) {
  return messages.some(
    (message) => message.role === "assistant" && hasActiveBrandAssistantStreamMetadata(message),
  );
}

export function getPersistedAssistantStreamActivityUpdates(
  messages: readonly UIMessage[],
): BrandAssistantActivityUpdateData[] {
  const activeStreamMessage = messages.find(
    (message) => message.role === "assistant" && hasActiveBrandAssistantStreamMetadata(message),
  );

  return activeStreamMessage
    ? getBrandAssistantActivityUpdatesFromParts(activeStreamMessage.parts)
    : [];
}

export function shouldShowAssistantRuntimeActivityForMessage(message: Pick<UIMessage, "parts">) {
  return (
    hasHiddenPendingAssistantWork(message) ||
    !hasRenderableAssistantMessageContent(message, { showActivityParts: false })
  );
}

export function hasHiddenPendingAssistantWork(message: Pick<UIMessage, "parts">) {
  return message.parts.some((part, index) => {
    if (!(part.type === "dynamic-tool" || part.type.startsWith("tool-"))) {
      return false;
    }

    const state = getAssistantToolState(part);
    if (state !== "input-streaming" && state !== "input-available") {
      return false;
    }

    return getAssistantTraceEntriesForPart(part, index).length === 0;
  });
}

export function appendAssistantLiveActivityUpdate(
  updates: readonly BrandAssistantActivityUpdateData[],
  update: BrandAssistantActivityUpdateData,
): BrandAssistantActivityUpdateData[] {
  const summary = update.summary.replace(/\s+/g, " ").trim();
  if (!summary) {
    return [...updates];
  }

  const nextUpdate = { ...update, summary };
  return [
    ...updates.filter((existingUpdate) => existingUpdate.summary.trim() !== summary),
    nextUpdate,
  ].slice(-ASSISTANT_LIVE_ACTIVITY_UPDATE_LIMIT);
}

export function shouldShowAssistantRuntimeStatus(status: AssistantChatStatus) {
  return status === "submitted" || status === "streaming";
}

export function getLatestUserMessageRuntimeKey(messages: Pick<UIMessage, "id" | "role">[]) {
  for (const message of messages.toReversed()) {
    if (message.role === "user") {
      return message.id;
    }
  }

  return null;
}

export function getAssistantMessageIdForRuntime(
  messages: Pick<UIMessage, "id" | "role">[],
  runtimeMessageKey: string,
) {
  const runtimeMessageIndex = messages.findIndex(
    (message) => message.id === runtimeMessageKey && message.role === "user",
  );
  if (runtimeMessageIndex === -1) {
    return null;
  }

  const assistantMessage = messages
    .slice(runtimeMessageIndex + 1)
    .find((message) => message.role === "assistant");

  return assistantMessage?.id ?? null;
}

export function isRenderableAssistantActivityPart(part: AssistantMessagePart) {
  if (part.type === "source-url") {
    return parseAssistantSourceReference(part) !== null;
  }

  const toolName = getAssistantToolName(part);
  if (toolName === "web_search" || toolName === "share_trace_note" || toolName === "show_tips") {
    return false;
  }

  return part.type === "dynamic-tool" || part.type.startsWith("tool-");
}

export type AssistantSourceReference = {
  id: string;
  url: string;
  title: string | null;
  domain: string;
};

export function parseAssistantSourceReference(part: unknown): AssistantSourceReference | null {
  if (!part || typeof part !== "object") {
    return null;
  }

  const candidate = part as {
    type?: unknown;
    sourceId?: unknown;
    url?: unknown;
    title?: unknown;
  };
  if (candidate.type !== "source-url" || typeof candidate.url !== "string") {
    return null;
  }

  const url = getSafeAssistantSourceUrl(candidate.url);
  const domain = getAssistantSourceDomain(url ?? undefined);
  if (!url || !domain) {
    return null;
  }

  const title = typeof candidate.title === "string" ? candidate.title.trim() : "";
  return {
    id: typeof candidate.sourceId === "string" ? candidate.sourceId : url,
    url,
    title: title || null,
    domain,
  };
}

export function getSafeAssistantSourceUrl(url: string | undefined): string | null {
  if (!url) {
    return null;
  }

  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.toString() : null;
  } catch {
    return null;
  }
}

export function getAssistantSourceDomain(url: string | undefined): string | null {
  const safeUrl = getSafeAssistantSourceUrl(url);
  if (!safeUrl) {
    return null;
  }

  return new URL(safeUrl).hostname.replace(/^www\./, "");
}

export function getAssistantToolName(part: unknown): string | null {
  if (!part || typeof part !== "object") {
    return null;
  }

  const candidate = part as { type?: unknown; toolName?: unknown };
  if (typeof candidate.toolName === "string" && candidate.type === "dynamic-tool") {
    return candidate.toolName;
  }

  if (typeof candidate.type === "string" && candidate.type.startsWith("tool-")) {
    return candidate.type.slice("tool-".length) || null;
  }

  return null;
}

export function getAssistantWebSearchQuery(value: unknown): string | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const candidate = value as { query?: unknown; action?: { query?: unknown } };
  const query = typeof candidate.query === "string" ? candidate.query : candidate.action?.query;
  return typeof query === "string" && query.trim().length > 0 ? query.trim() : null;
}

export function getAssistantWebSearchSourceCount(output: unknown): number | null {
  if (!output || typeof output !== "object") {
    return null;
  }

  const candidate = output as { sources?: unknown };
  return Array.isArray(candidate.sources) ? candidate.sources.length : null;
}

export function getAssistantKnowledgeBaseQuery(value: unknown): string | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const candidate = value as { query?: unknown; page?: unknown };
  const query = typeof candidate.query === "string" ? candidate.query : candidate.page;
  return typeof query === "string" && query.trim().length > 0 ? query.trim() : null;
}

export function getAssistantKnowledgeBaseResultCount(output: unknown): number | null {
  if (!output || typeof output !== "object") {
    return null;
  }

  const candidate = output as { resultCount?: unknown };
  return typeof candidate.resultCount === "number" && Number.isFinite(candidate.resultCount)
    ? candidate.resultCount
    : null;
}

export function getLatestMessageScrollKey(messages: UIMessage[]) {
  const latestMessage = messages.at(-1);
  if (!latestMessage) {
    return "empty";
  }

  return `${latestMessage.id}:${latestMessage.role}:${getAssistantMessageScrollSignature(latestMessage)}`;
}

export function getAssistantThreadScrollKey(messages: Pick<UIMessage, "id">[]) {
  return messages[0]?.id ?? "empty";
}

export type AssistantThreadScrollAction =
  | { type: "anchor-user"; messageId: string }
  | { type: "scroll-to-latest" }
  | { type: "preserve" };

export function getAssistantThreadScrollAction(input: {
  isBusy: boolean;
  latestUserMessageId: string | null;
  anchoredUserMessageId: string | null;
  threadLineageChanged: boolean;
}): AssistantThreadScrollAction {
  if (
    input.isBusy &&
    input.latestUserMessageId &&
    input.latestUserMessageId !== input.anchoredUserMessageId
  ) {
    return { type: "anchor-user", messageId: input.latestUserMessageId };
  }

  if (input.threadLineageChanged) {
    return input.isBusy && input.latestUserMessageId
      ? { type: "anchor-user", messageId: input.latestUserMessageId }
      : { type: "scroll-to-latest" };
  }

  return { type: "preserve" };
}

export function getAssistantUserAnchorScrollTop(input: {
  currentScrollTop: number;
  scrollRootTop: number;
  messageTop: number;
  topInset: number;
}) {
  return Math.max(
    0,
    input.currentScrollTop + input.messageTop - input.scrollRootTop - input.topInset,
  );
}

export function getAssistantRealContentEnd(input: {
  currentScrollTop: number;
  scrollRootTop: number;
  contentBottom: number;
  bottomInset: number;
}) {
  return Math.max(
    0,
    input.currentScrollTop + input.contentBottom - input.scrollRootTop + input.bottomInset,
  );
}

export function getAssistantLatestScrollTop(input: {
  clientHeight: number;
  realContentEnd: number;
}) {
  return Math.max(0, input.realContentEnd - input.clientHeight);
}

export function getAssistantRunwayHeight(input: {
  anchorScrollTop: number;
  clientHeight: number;
  realContentEnd: number;
}) {
  return Math.max(0, input.anchorScrollTop + input.clientHeight - input.realContentEnd);
}

export function getLatestAssistantMessageAfterLatestUser(messages: readonly UIMessage[]) {
  const latestUserIndex = messages.findLastIndex((message) => message.role === "user");
  for (let index = messages.length - 1; index > latestUserIndex; index -= 1) {
    const message = messages[index];
    if (message?.role === "assistant") {
      return message;
    }
  }

  return null;
}

export function getLatestAssistantTipsAfterLatestUser(
  messages: readonly UIMessage[],
): BrandAssistantTipsData | null {
  const message = getLatestAssistantMessageAfterLatestUser(messages);
  if (!message) {
    return null;
  }

  return getAssistantTips(message);
}

export function getAssistantTips(message: Pick<UIMessage, "parts">): BrandAssistantTipsData | null {
  const dataPartTips = getBrandAssistantTipsFromParts(message.parts).at(-1);
  if (dataPartTips) {
    return dataPartTips;
  }

  for (const part of message.parts.toReversed()) {
    if (getAssistantToolName(part) !== "show_tips") {
      continue;
    }

    if (getAssistantToolState(part) !== "output-available") {
      continue;
    }

    const parsed = brandAssistantTipsDataSchema.safeParse(getAssistantToolOutput(part));
    if (parsed.success) {
      return parsed.data;
    }
  }

  return null;
}

export function hasActiveAssistantMessagePart(message: UIMessage) {
  if (message.role !== "assistant") {
    return false;
  }

  return message.parts.some((part) => {
    const state = (part as { state?: unknown }).state;
    return state === "streaming" || state === "input-streaming" || state === "input-available";
  });
}

export function shouldRenderAssistantStructuredExtras(input: {
  isBusy: boolean;
  messageId: string;
  latestAssistantMessageId: string | null;
  hasActiveMessagePart: boolean;
}) {
  return (
    !input.hasActiveMessagePart &&
    (!input.isBusy || input.messageId !== input.latestAssistantMessageId)
  );
}

export function isAtLatestMessageEdge(
  viewport: Pick<HTMLElement, "clientHeight" | "scrollTop">,
  realContentEnd: number,
) {
  const distanceFromBottom = realContentEnd - viewport.scrollTop - viewport.clientHeight;
  return distanceFromBottom <= LATEST_MESSAGE_SCROLL_THRESHOLD_PX;
}

export function shouldShowJumpToLatest(
  viewport: Pick<HTMLElement, "clientHeight" | "scrollTop">,
  realContentEnd: number,
) {
  return !isAtLatestMessageEdge(viewport, realContentEnd);
}

export function getInternalAssistantHref(href: string | undefined): string | null {
  if (href === "/app" || href?.startsWith("/app/")) {
    return href;
  }

  if (!href) {
    return null;
  }

  try {
    const url = new URL(href);
    const internalHref = `${url.pathname}${url.search}${url.hash}`;

    return internalHref === "/app" || internalHref.startsWith("/app/") ? internalHref : null;
  } catch {
    return null;
  }
}

export function isInternalAssistantHref(href: string | undefined): href is string {
  return getInternalAssistantHref(href) !== null;
}

export function getSafeAssistantImageSrc(src: string | undefined): string | null {
  if (!src) {
    return null;
  }

  try {
    const url = new URL(src);
    return url.protocol === "http:" || url.protocol === "https:" ? src : null;
  } catch {
    return null;
  }
}

function getAssistantMessageScrollSignature(message: UIMessage) {
  return message.parts
    .map((part) => {
      if (part.type === "text" || part.type === "reasoning") {
        return `${part.type}:${part.text.length}`;
      }

      if (part.type === "source-url") {
        const source = parseAssistantSourceReference(part);
        return source ? `source-url:${source.id}:${source.url}` : "source-url:invalid";
      }

      if (part.type === "dynamic-tool" || part.type.startsWith("tool-")) {
        return [
          part.type,
          getAssistantToolState(part),
          getAssistantToolCallId(part),
          getAssistantWebSearchQuery(getAssistantToolInput(part)),
          getAssistantWebSearchSourceCount(getAssistantToolOutput(part)),
          getAssistantKnowledgeBaseQuery(getAssistantToolInput(part)),
          getAssistantKnowledgeBaseResultCount(getAssistantToolOutput(part)),
          getAssistantToolErrorText(part),
        ].join(":");
      }

      const selectedAssetReferences = getBrandAssistantSelectedAssetReferencesFromParts([part]);
      if (selectedAssetReferences.length > 0) {
        const reference = selectedAssetReferences[0]!;
        return [
          part.type,
          reference.generatedAssetId,
          reference.blockId,
          reference.slideIndex,
          reference.signedUrl,
        ].join(":");
      }

      const compactionEvent = parseBrandAssistantChatCompactionEventPart(part);
      if (compactionEvent) {
        return [
          part.type,
          compactionEvent.data.summarizedUntilMessageId,
          compactionEvent.data.summarizedMessageCount,
        ].join(":");
      }

      return part.type;
    })
    .join("|");
}

export function getAssistantActivityPartKey(part: AssistantMessagePart, index: number) {
  if (part.type === "source-url") {
    return parseAssistantSourceReference(part)?.id ?? `source-${index}`;
  }

  return getAssistantToolCallId(part) ?? `${part.type}-${index}`;
}

export function getAssistantToolCallId(part: unknown): string | null {
  if (!part || typeof part !== "object") {
    return null;
  }

  const candidate = part as { toolCallId?: unknown };
  return typeof candidate.toolCallId === "string" ? candidate.toolCallId : null;
}

export function getAssistantToolState(part: unknown): string | null {
  if (!part || typeof part !== "object") {
    return null;
  }

  const candidate = part as { state?: unknown };
  return typeof candidate.state === "string" ? candidate.state : null;
}

export function getAssistantToolInput(part: unknown): unknown {
  return part && typeof part === "object" && "input" in part ? part.input : undefined;
}

export function getAssistantToolOutput(part: unknown): unknown {
  return part && typeof part === "object" && "output" in part ? part.output : undefined;
}

export function getAssistantToolErrorText(part: unknown): string | null {
  if (!part || typeof part !== "object") {
    return null;
  }

  const candidate = part as { errorText?: unknown };
  return typeof candidate.errorText === "string" && candidate.errorText.trim().length > 0
    ? candidate.errorText.trim()
    : null;
}

function getAssistantWebSearchTraceDetail(input: {
  sourceCount: number | null;
  status: AssistantTraceEntry["state"];
}) {
  if (input.status === "pending") {
    return null;
  }

  if (input.status === "error") {
    return "search failed";
  }

  if (input.sourceCount === 0) {
    return "limited public signal";
  }

  return null;
}

function getAssistantKnowledgeBaseTraceDetail(resultCount: number | null) {
  if (resultCount === null) {
    return null;
  }

  if (resultCount === 0) {
    return "no close match";
  }

  return null;
}

function getAssistantTraceState(state: string | null): AssistantTraceEntry["state"] {
  if (state === "output-error") {
    return "error";
  }

  if (state === "output-available") {
    return "done";
  }

  return "pending";
}

function getAssistantTraceNote(value: unknown): {
  kind: string;
  detail: string;
  query: string | null;
} | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const record = value as Record<string, unknown>;
  const kind = typeof record.kind === "string" ? record.kind.trim() : "";
  const detail = typeof record.detail === "string" ? record.detail.trim() : "";
  const query = typeof record.query === "string" ? record.query.trim() : "";
  if (!kind || !detail) {
    return null;
  }

  return { kind, detail, query: query || null };
}

export function getAssistantToolLabel(toolName: string, intl: Pick<IntlShape, "formatMessage">) {
  if (toolName === "web_search") {
    return intl.formatMessage({ id: "assistantChat.tool.webSearch", defaultMessage: "Web search" });
  }

  if (toolName === "search_knowledge_base" || toolName === "get_knowledge_base_page") {
    return intl.formatMessage({
      id: "assistantChat.tool.explanation",
      defaultMessage: "explanation",
    });
  }

  if (toolName === "preview_standalone_asset_batch_creation") {
    return intl.formatMessage({
      id: "assistantChat.tool.assetBatchProposal",
      defaultMessage: "assets",
    });
  }

  if (toolName === "preview_audience_profile_creation") {
    return intl.formatMessage({
      id: "assistantChat.tool.audienceProfileProposal",
      defaultMessage: "audience profile",
    });
  }

  if (toolName === "fine_tune_generated_asset_image") {
    return intl.formatMessage({
      id: "assistantChat.tool.imageEdit",
      defaultMessage: "image update",
    });
  }

  if (toolName === "preview_generated_asset_image_fine_tune") {
    return intl.formatMessage({
      id: "assistantChat.tool.imageEditProposal",
      defaultMessage: "image edit",
    });
  }

  return prettifyAssistantToolName(toolName);
}

export function getAssistantToolStatusLabel({
  toolName,
  toolLabel,
  state,
  intl,
  topic,
}: {
  toolName: string;
  toolLabel: string;
  state: string | null;
  intl: Pick<IntlShape, "formatMessage">;
  topic?: string | null;
}) {
  const topicValue = topic?.trim();

  if (toolName === "web_search") {
    if (state === "output-available") {
      return intl.formatMessage({
        id: "assistantChat.tool.webSearchComplete",
        defaultMessage: "Web search complete",
      });
    }

    if (state === "output-error") {
      return intl.formatMessage({
        id: "assistantChat.tool.webSearchFailed",
        defaultMessage: "Web search failed",
      });
    }

    return intl.formatMessage({
      id: "assistantChat.tool.searchingWeb",
      defaultMessage: "Searching the web",
    });
  }

  if (toolName === "search_knowledge_base") {
    if (state === "output-available") {
      return topicValue
        ? intl.formatMessage(
            {
              id: "assistantChat.tool.explainingTopic",
              defaultMessage: "Explaining {topic}",
            },
            { topic: topicValue },
          )
        : intl.formatMessage({
            id: "assistantChat.tool.explaining",
            defaultMessage: "Explaining the topic",
          });
    }

    if (state === "output-error") {
      return intl.formatMessage({
        id: "assistantChat.tool.explanationFailed",
        defaultMessage: "Couldn't explain that topic",
      });
    }

    return topicValue
      ? intl.formatMessage(
          {
            id: "assistantChat.tool.preparingExplanationTopic",
            defaultMessage: "Preparing an explanation about {topic}",
          },
          { topic: topicValue },
        )
      : intl.formatMessage({
          id: "assistantChat.tool.preparingExplanation",
          defaultMessage: "Preparing an explanation",
        });
  }

  if (toolName === "get_knowledge_base_page") {
    if (state === "output-available") {
      return topicValue
        ? intl.formatMessage(
            {
              id: "assistantChat.tool.addingContextTopic",
              defaultMessage: "Adding more context about {topic}",
            },
            { topic: topicValue },
          )
        : intl.formatMessage({
            id: "assistantChat.tool.addingContext",
            defaultMessage: "Adding more context",
          });
    }

    if (state === "output-error") {
      return intl.formatMessage({
        id: "assistantChat.tool.contextLookupFailed",
        defaultMessage: "Couldn't add context for that topic",
      });
    }

    return topicValue
      ? intl.formatMessage(
          {
            id: "assistantChat.tool.gettingMoreContextTopic",
            defaultMessage: "Getting more context about {topic}",
          },
          { topic: topicValue },
        )
      : intl.formatMessage({
          id: "assistantChat.tool.gettingMoreContext",
          defaultMessage: "Getting more context",
        });
  }

  if (toolName === "preview_standalone_asset_batch_creation") {
    if (state === "output-available") {
      return intl.formatMessage({
        id: "assistantChat.tool.assetsProposed",
        defaultMessage: "Assets proposed",
      });
    }

    if (state === "output-error") {
      return intl.formatMessage({
        id: "assistantChat.tool.assetsProposalFailed",
        defaultMessage: "Asset proposal failed",
      });
    }

    return intl.formatMessage({
      id: "assistantChat.tool.proposingAssets",
      defaultMessage: "Proposing assets",
    });
  }

  if (toolName === "preview_audience_profile_creation") {
    if (state === "output-available") {
      return intl.formatMessage({
        id: "assistantChat.tool.audienceProfileProposed",
        defaultMessage: "Audience profile proposed",
      });
    }

    if (state === "output-error") {
      return intl.formatMessage({
        id: "assistantChat.tool.audienceProfileProposalFailed",
        defaultMessage: "Audience profile proposal failed",
      });
    }

    return intl.formatMessage({
      id: "assistantChat.tool.preparingAudienceProfile",
      defaultMessage: "Preparing audience profile",
    });
  }

  if (toolName === "fine_tune_generated_asset_image") {
    if (state === "output-available") {
      return intl.formatMessage({
        id: "assistantChat.tool.imageUpdateQueued",
        defaultMessage: "Image update queued",
      });
    }

    if (state === "output-error") {
      return intl.formatMessage({
        id: "assistantChat.tool.imageUpdateFailed",
        defaultMessage: "Image update failed",
      });
    }

    return intl.formatMessage({
      id: "assistantChat.tool.queueingImageUpdate",
      defaultMessage: "Queueing image update",
    });
  }

  if (toolName === "preview_generated_asset_image_fine_tune") {
    if (state === "output-available") {
      return intl.formatMessage({
        id: "assistantChat.tool.imageEditProposed",
        defaultMessage: "Image edit proposed",
      });
    }

    if (state === "output-error") {
      return intl.formatMessage({
        id: "assistantChat.tool.imageEditProposalFailed",
        defaultMessage: "Image edit proposal failed",
      });
    }

    return intl.formatMessage({
      id: "assistantChat.tool.preparingImageEdit",
      defaultMessage: "Preparing image edit",
    });
  }

  if (state === "output-available") {
    return intl.formatMessage(
      { id: "assistantChat.tool.toolComplete", defaultMessage: "{tool} complete" },
      { tool: toolLabel },
    );
  }

  if (state === "output-error") {
    return intl.formatMessage(
      { id: "assistantChat.tool.toolFailed", defaultMessage: "{tool} failed" },
      { tool: toolLabel },
    );
  }

  return intl.formatMessage(
    { id: "assistantChat.tool.usingTool", defaultMessage: "Using {tool}" },
    { tool: toolLabel },
  );
}

function prettifyAssistantToolName(toolName: string) {
  return toolName.split(/[_-]+/).filter(Boolean).join(" ") || "Tool";
}
